import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './Projects.css'; // Reuse the same styles

const API_BASE_URL = 'http://72.61.235.16:5001/api/v1';

const Users = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'customer',
  });

  const roles = [
    { value: 'super_admin', label: 'Super Admin' },
    { value: 'admin', label: 'Admin' },
    { value: 'project_manager', label: 'Project Manager' },
    { value: 'site_incharge', label: 'Site Incharge' },
    { value: 'finance', label: 'Finance' },
    { value: 'customer', label: 'Customer' },
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/users`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('User created successfully!');
      setShowCreateModal(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        password: '',
        role: 'customer',
      });
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create user');
    }
  };

  const handleDeactivateUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to deactivate "${userName}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_BASE_URL}/users/${userId}/deactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('User deactivated successfully!');
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to deactivate user');
    }
  };

  const getRoleColor = (role) => {
    const roleColors = {
      super_admin: '#E53935',
      admin: '#1E88E5',
      project_manager: '#8E24AA',
      site_incharge: '#00C853',
      finance: '#FF6F00',
      customer: '#757575',
    };
    return roleColors[role] || '#757575';
  };

  const filteredUsers = users.filter((user) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query) ||
      user.phone.toLowerCase().includes(query) ||
      user.role.toLowerCase().includes(query)
    );
  });

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-subtitle">Manage system users and roles</p>
        </div>
        <div className="header-actions">
          {user?.role === 'super_admin' && (
            <button className="btn-warning" onClick={() => navigate('/create-admin')}>
              <i className="fas fa-user-plus"></i> Add Admin
            </button>
          )}
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
            <i className="fas fa-plus"></i> Create User
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <i className="fas fa-search search-icon"></i>
        <input
          type="text"
          className="search-input"
          placeholder="Search by name, email, phone, or role..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Users List */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-users empty-icon"></i>
          <p>No users found</p>
        </div>
      ) : (
        <div className="projects-grid">
          {filteredUsers.map((user) => (
            <div key={user.id} className="project-card">
              <div className="project-header">
                <h3 className="project-name">{user.name}</h3>
                {user.is_active && (
                  <button
                    className="btn-delete"
                    onClick={() => handleDeactivateUser(user.id, user.name)}
                    title="Deactivate user"
                  >
                    <i className="fas fa-ban"></i>
                  </button>
                )}
              </div>
              <p className="project-location">
                <i className="fas fa-envelope"></i> {user.email}
              </p>
              <p className="project-location">
                <i className="fas fa-phone"></i> {user.phone}
              </p>
              <div className="project-status" style={{ backgroundColor: getRoleColor(user.role) }}>
                {user.role.replace(/_/g, ' ')}
              </div>
              <div className="project-footer">
                <span className="project-date">
                  {user.is_active ? (
                    <><i className="fas fa-check-circle" style={{ color: '#00C853' }}></i> Active</>
                  ) : (
                    <><i className="fas fa-times-circle" style={{ color: '#E53935' }}></i> Inactive</>
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New User</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleCreateUser} className="modal-form">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter name"
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email"
                />
              </div>
              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Enter phone"
                />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  required
                  minLength="6"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter password (min 6 characters)"
                />
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select
                  required
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  {roles.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
